/**
 * 1D transfer functions: the desktop `.tf` JSON, baked to a 1024-wide RGBA float texture the
 * way TransferFunction.GenerateTexture does (smoothstep ease between control points, colours
 * stored linear, alpha from the alpha points, optional domain shift/stretch about a pivot).
 */
import * as THREE from 'three';

export interface TfColourPoint {
  dataValue: number;
  colourValue: { r: number; g: number; b: number; a?: number };
}
export interface TfAlphaPoint {
  dataValue: number;
  alphaValue: number;
}
export interface TransferFunctionJson {
  version?: number;
  colourPoints: TfColourPoint[];
  alphaPoints: TfAlphaPoint[];
}

export const TF_WIDTH = 1024;

function srgbToLinear(c: number): number {
  return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

function smoothStep(t: number): number {
  const x = Math.min(1, Math.max(0, t));
  return x * x * (3 - 2 * x);
}

export interface TfDomain {
  shift: number;
  scale: number;
  pivot: number;
}

export const NEUTRAL_DOMAIN: TfDomain = { shift: 0, scale: 1, pivot: 0.5 };

/** TransferFunctionDomain.SampleCoordinate: texel t reads the base curve at clamp01(base(t)). */
export function sampleCoordinate(t: number, d: TfDomain): number {
  if (d.shift === 0 && d.scale === 1) return t;
  const scale = Math.min(20, Math.max(0.05, d.scale));
  const u = d.pivot + (t - d.shift - d.pivot) / scale;
  return Math.min(1, Math.max(0, u));
}

/** Bakes the curve to Float32 RGBA (width 1024, height 1). rgb linear, premultiplied: no. */
export function bakeTransferFunction(tf: TransferFunctionJson, domain: TfDomain = NEUTRAL_DOMAIN): Float32Array {
  const cols = tf.colourPoints.map((p) => ({ v: p.dataValue, c: p.colourValue })).sort((a, b) => a.v - b.v);
  const alphas = tf.alphaPoints.map((p) => ({ v: p.dataValue, a: p.alphaValue })).sort((a, b) => a.v - b.v);
  if (cols.length === 0 || cols[cols.length - 1].v < 1) cols.push({ v: 1, c: { r: 1, g: 1, b: 1 } });
  if (cols[0].v > 0) cols.unshift({ v: 0, c: { r: 1, g: 1, b: 1 } });
  if (alphas.length === 0 || alphas[alphas.length - 1].v < 1) alphas.push({ v: 1, a: 1 });
  if (alphas[0].v > 0) alphas.unshift({ v: 0, a: 0 });

  const data = new Float32Array(TF_WIDTH * 4);
  let ic = 0;
  let ia = 0;
  for (let x = 0; x < TF_WIDTH; x++) {
    const t = x / (TF_WIDTH - 1);
    const u = sampleCoordinate(t, domain);
    while (ic < cols.length - 2 && cols[ic + 1].v < u) ic++;
    while (ia < alphas.length - 2 && alphas[ia + 1].v < u) ia++;
    const lc = cols[ic];
    const rc = cols[ic + 1];
    const la = alphas[ia];
    const ra = alphas[ia + 1];
    const cSpan = rc.v - lc.v;
    const aSpan = ra.v - la.v;
    let tc = cSpan > 1e-6 ? (Math.min(Math.max(u, lc.v), rc.v) - lc.v) / cSpan : 1;
    let ta = aSpan > 1e-6 ? (Math.min(Math.max(u, la.v), ra.v) - la.v) / aSpan : 1;
    tc = smoothStep(tc);
    ta = smoothStep(ta);
    const r = rc.c.r * tc + lc.c.r * (1 - tc);
    const g = rc.c.g * tc + lc.c.g * (1 - tc);
    const b = rc.c.b * tc + lc.c.b * (1 - tc);
    const a = ra.a * ta + la.a * (1 - ta);
    data[x * 4] = srgbToLinear(r);
    data[x * 4 + 1] = srgbToLinear(g);
    data[x * 4 + 2] = srgbToLinear(b);
    data[x * 4 + 3] = a;
  }
  return data;
}

export function makeTfTexture(data: Float32Array): THREE.DataTexture {
  const tex = new THREE.DataTexture(data as Float32Array<ArrayBuffer>, TF_WIDTH, 1, THREE.RGBAFormat, THREE.FloatType);
  tex.minFilter = THREE.LinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.wrapS = tex.wrapT = THREE.ClampToEdgeWrapping;
  tex.generateMipmaps = false;
  tex.colorSpace = THREE.NoColorSpace;
  tex.needsUpdate = true;
  return tex;
}

export function updateTfTexture(tex: THREE.DataTexture, data: Float32Array): void {
  (tex.image.data as unknown as Float32Array).set(data);
  tex.needsUpdate = true;
}

/** Sample the baked curve on the CPU (for the histogram editor / previews). */
export function sampleBaked(data: Float32Array, t: number): [number, number, number, number] {
  const x = Math.min(TF_WIDTH - 1, Math.max(0, Math.round(t * (TF_WIDTH - 1))));
  return [data[x * 4], data[x * 4 + 1], data[x * 4 + 2], data[x * 4 + 3]];
}

/** HU helpers for the default encoding (-1024..3071 -> 0..1). */
export const HU_MIN = -1024;
export const HU_MAX = 3071;
export const huToNormalized = (hu: number, min = HU_MIN, max = HU_MAX) => Math.min(1, Math.max(0, (hu - min) / (max - min)));
export const normalizedToHu = (t: number, min = HU_MIN, max = HU_MAX) => min + Math.min(1, Math.max(0, t)) * (max - min);

/** Approximation of the desktop's first-visible-alpha threshold on the TF axis, used for the
 * 32^3 occupancy skip (not yet used) and for empty-space checks. */
export function firstVisibleDensity(data: Float32Array): number {
  for (let x = 0; x < TF_WIDTH; x++) if (data[x * 4 + 3] > 0) return x / (TF_WIDTH - 1);
  return 1;
}
